import type { WhatsappMetaEntity } from "@repo/common";
import { deleteWhatsappSender, saveWhatsappSender, useAuth } from "@repo/ui";
import { getFirestore } from "firebase/firestore";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import type { WhatsappSender } from "../types";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "./ui/alert-dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "./ui/table";

type SenderForm = {
	metaPhoneNumberId: string;
	businessPhoneNumber: string;
	wabaId: string;
	companyId: string;
	agentId: string;
	metaEntity: WhatsappMetaEntity;
	status: "active" | "disabled";
};

const emptyForm = (): SenderForm => ({
	metaPhoneNumberId: "",
	businessPhoneNumber: "",
	wabaId: "",
	companyId: "",
	agentId: "",
	metaEntity: "danj-consulting",
	status: "active",
});

export function WhatsappSendersPage() {
	const db = getFirestore();
	const { user } = useAuth();
	const { whatsappSenders, companies, agents } = useData();
	const [search, setSearch] = useState("");
	const [form, setForm] = useState<SenderForm>(emptyForm);
	const [editing, setEditing] = useState<WhatsappSender | null>(null);
	const [deleting, setDeleting] = useState<WhatsappSender | null>(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [saving, setSaving] = useState(false);

	const companyNames = useMemo(
		() => new Map(companies.map((company) => [company.id, company.name])),
		[companies],
	);
	const agentNames = useMemo(
		() => new Map(agents.map((agent) => [agent.id, agent.name])),
		[agents],
	);
	const availableAgents = useMemo(
		() => agents.filter((agent) => agent.companyId === form.companyId),
		[agents, form.companyId],
	);
	const filteredSenders = useMemo(() => {
		const needle = search.trim().toLowerCase();
		if (!needle) return whatsappSenders;
		return whatsappSenders.filter((sender) =>
			[
				sender.metaPhoneNumberId,
				sender.businessPhoneNumber,
				sender.wabaId,
				sender.companyId,
				sender.agentId,
				sender.metaEntity,
				sender.status,
				companyNames.get(sender.companyId),
				agentNames.get(sender.agentId ?? ""),
			].some((value) => value?.toLowerCase().includes(needle)),
		);
	}, [agentNames, companyNames, search, whatsappSenders]);

	const openCreate = () => {
		setEditing(null);
		setForm(emptyForm());
		setDialogOpen(true);
	};

	const openEdit = (sender: WhatsappSender) => {
		setEditing(sender);
		setForm({
			metaPhoneNumberId: sender.metaPhoneNumberId,
			businessPhoneNumber: sender.businessPhoneNumber,
			wabaId: sender.wabaId ?? "",
			companyId: sender.companyId,
			agentId: sender.agentId ?? "",
			metaEntity: sender.metaEntity,
			status: sender.status,
		});
		setDialogOpen(true);
	};

	const handleCompanyChange = (companyId: string) => {
		setForm((current) => ({
			...current,
			companyId,
			agentId:
				agents.find(
					(agent) =>
						agent.id === current.agentId && agent.companyId === companyId,
				)?.id ?? "",
		}));
	};

	const handleSave = async () => {
		const metaPhoneNumberId = form.metaPhoneNumberId.trim();
		const businessPhoneNumber = form.businessPhoneNumber.trim();
		if (!/^\d+$/.test(metaPhoneNumberId)) {
			toast.error("Meta phone number ID must contain digits only");
			return;
		}
		if (
			!editing &&
			whatsappSenders.some(
				(sender) => sender.metaPhoneNumberId === metaPhoneNumberId,
			)
		) {
			toast.error("That Meta phone number ID is already configured");
			return;
		}
		if (!/^\+\d+$/.test(businessPhoneNumber)) {
			toast.error(
				"Business phone number must use E.164 format, for example +972...",
			);
			return;
		}
		if (form.wabaId.trim() && !/^\d+$/.test(form.wabaId.trim())) {
			toast.error("WABA ID must contain digits only");
			return;
		}
		if (!form.companyId || !form.agentId) {
			toast.error("Select a company and agent");
			return;
		}
		if (
			!agents.some(
				(agent) =>
					agent.id === form.agentId && agent.companyId === form.companyId,
			)
		) {
			toast.error("The selected agent does not belong to the selected company");
			return;
		}

		setSaving(true);
		try {
			await saveWhatsappSender(
				db,
				{
					metaPhoneNumberId,
					businessPhoneNumber,
					wabaId: form.wabaId.trim() || null,
					companyId: form.companyId,
					agentId: form.agentId,
					metaEntity: form.metaEntity,
					status: form.status,
				},
				editing?.metaPhoneNumberId,
			);
			toast.success(`WhatsApp sender ${editing ? "updated" : "created"}`);
			setDialogOpen(false);
		} catch (error) {
			console.error("Failed to save WhatsApp sender", error);
			toast.error("Failed to save WhatsApp sender");
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async () => {
		if (!deleting) return;
		try {
			await deleteWhatsappSender(db, deleting.metaPhoneNumberId);
			toast.success("WhatsApp sender deleted");
			setDeleting(null);
		} catch (error) {
			console.error("Failed to delete WhatsApp sender", error);
			toast.error("Failed to delete WhatsApp sender");
		}
	};

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between gap-4">
				<div>
					<h1 className="text-3xl font-bold">WhatsApp Senders</h1>
					<p className="text-slate-600">
						Route Meta phone-number IDs to companies, agents, and credentials.
					</p>
				</div>
				<Button onClick={openCreate}>
					<Plus className="mr-2 h-4 w-4" />
					Add sender
				</Button>
			</div>

			<Card>
				<CardHeader>
					<div className="flex items-center justify-between gap-4">
						<CardTitle>Configured senders</CardTitle>
						<div className="relative w-full max-w-sm">
							<Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
							<Input
								value={search}
								onChange={(event) => setSearch(event.target.value)}
								placeholder="Search senders"
								className="pl-9"
							/>
						</div>
					</div>
				</CardHeader>
				<CardContent>
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Meta phone ID</TableHead>
								<TableHead>Phone number</TableHead>
								<TableHead>Company</TableHead>
								<TableHead>Agent</TableHead>
								<TableHead>Meta entity</TableHead>
								<TableHead>Status</TableHead>
								<TableHead className="text-right">Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{filteredSenders.map((sender) => (
								<TableRow key={sender.metaPhoneNumberId}>
									<TableCell className="font-mono">
										{sender.metaPhoneNumberId}
									</TableCell>
									<TableCell>{sender.businessPhoneNumber}</TableCell>
									<TableCell>
										{companyNames.get(sender.companyId) ?? sender.companyId}
									</TableCell>
									<TableCell>
										{sender.agentId
											? (agentNames.get(sender.agentId) ?? sender.agentId)
											: "Unassigned"}
									</TableCell>
									<TableCell>
										<Badge variant="outline">{sender.metaEntity}</Badge>
									</TableCell>
									<TableCell>
										<Badge
											variant={
												sender.status === "active" ? "default" : "secondary"
											}
										>
											{sender.status}
										</Badge>
									</TableCell>
									<TableCell>
										<div className="flex justify-end gap-2">
											<Button
												variant="ghost"
												size="icon"
												onClick={() => openEdit(sender)}
												aria-label={`Edit ${sender.metaPhoneNumberId}`}
											>
												<Pencil className="h-4 w-4" />
											</Button>
											<Button
												variant="ghost"
												size="icon"
												onClick={() => setDeleting(sender)}
												aria-label={`Delete ${sender.metaPhoneNumberId}`}
											>
												<Trash2 className="h-4 w-4 text-red-600" />
											</Button>
										</div>
									</TableCell>
								</TableRow>
							))}
							{filteredSenders.length === 0 && (
								<TableRow>
									<TableCell colSpan={7} className="py-10 text-center">
										No WhatsApp senders found.
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</CardContent>
			</Card>

			<Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
				<DialogContent className="admin-editor-dialog">
					<DialogHeader>
						<DialogTitle>
							{editing ? "Edit WhatsApp sender" : "Add WhatsApp sender"}
						</DialogTitle>
						<DialogDescription>
							The Meta phone-number ID is the routing key received in webhook
							metadata.
						</DialogDescription>
					</DialogHeader>
					<div className="admin-editor-body grid gap-4">
						<div className="grid gap-2">
							<Label htmlFor="meta-phone-number-id">Meta phone-number ID</Label>
							<Input
								id="meta-phone-number-id"
								value={form.metaPhoneNumberId}
								disabled={Boolean(editing)}
								onChange={(event) =>
									setForm({ ...form, metaPhoneNumberId: event.target.value })
								}
							/>
						</div>
						<div className="grid gap-2">
							<Label htmlFor="business-phone-number">
								Business phone number
							</Label>
							<Input
								id="business-phone-number"
								placeholder="+972..."
								value={form.businessPhoneNumber}
								onChange={(event) =>
									setForm({ ...form, businessPhoneNumber: event.target.value })
								}
							/>
						</div>
						<div className="grid gap-2">
							<Label htmlFor="waba-id">WABA ID</Label>
							<Input
								id="waba-id"
								value={form.wabaId}
								onChange={(event) =>
									setForm({ ...form, wabaId: event.target.value })
								}
							/>
						</div>
						<div className="grid gap-4 sm:grid-cols-2">
							<div className="grid gap-2">
								<Label>Company</Label>
								<Select
									value={form.companyId}
									onValueChange={handleCompanyChange}
								>
									<SelectTrigger>
										<SelectValue placeholder="Select company" />
									</SelectTrigger>
									<SelectContent>
										{companies.map((company) => (
											<SelectItem key={company.id} value={company.id}>
												{company.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div className="grid gap-2">
								<Label>Agent</Label>
								<Select
									value={form.agentId}
									onValueChange={(agentId) => setForm({ ...form, agentId })}
									disabled={!form.companyId}
								>
									<SelectTrigger>
										<SelectValue placeholder="Select agent" />
									</SelectTrigger>
									<SelectContent>
										{availableAgents.map((agent) => (
											<SelectItem key={agent.id} value={agent.id}>
												{agent.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>
						<div className="grid gap-4 sm:grid-cols-2">
							<div className="grid gap-2">
								<Label>Meta entity</Label>
								<Select
									value={form.metaEntity}
									onValueChange={(metaEntity: WhatsappMetaEntity) =>
										setForm({ ...form, metaEntity })
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="danj-consulting">
											danj-consulting
										</SelectItem>
										<SelectItem value="provoize">provoize</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div className="grid gap-2">
								<Label>Status</Label>
								<Select
									value={form.status}
									onValueChange={(status: "active" | "disabled") =>
										setForm({ ...form, status })
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="active">Active</SelectItem>
										<SelectItem value="disabled">Disabled</SelectItem>
									</SelectContent>
								</Select>
							</div>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setDialogOpen(false)}>
							Cancel
						</Button>
						<Button onClick={handleSave} disabled={saving || !user}>
							{saving ? "Saving..." : "Save"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<AlertDialog
				open={Boolean(deleting)}
				onOpenChange={(open) => !open && setDeleting(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Delete WhatsApp sender?</AlertDialogTitle>
						<AlertDialogDescription>
							Inbound messages for {deleting?.businessPhoneNumber} will stop
							routing immediately.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
